import {
  configureStore,
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit"
import axios from "axios"
import isEqual from "lodash/isEqual"
import { useDispatch } from "react-redux"
import { Cycle, Space, User } from "~/types"

/* ===== ===== TYPES ===== ===== */

export interface CycleState {
  currentCycle: Cycle
}

export interface UserState {
  user: User | null
  fetching: boolean
}

export interface SpacesState {
  spaces: Space[]
  spaceName: string | null
}

export interface AlertsState {
  alerts: Array<{ message: string; type: string }>
}

/* ===== ===== CYCLE ===== ===== */

const cycleSlice = createSlice({
  name: "cycle",
  initialState: {
    currentCycle: "work",
  } as CycleState,
  reducers: {
    setCycle: (state, action) => {
      state.currentCycle = action.payload
    },
    switchCycle: (state) => {
      const nextCycle = state.currentCycle === "work" ? "break" : "work"
      state.currentCycle = nextCycle
    },
  },
})

export const { setCycle, switchCycle } = cycleSlice.actions
export const getCycle = (state: RootState) => state.cycleReducer.currentCycle

export const cycleReducer = cycleSlice.reducer

/* ===== ===== USER ===== ===== */

export const fetchUser = createAsyncThunk<User | null>(
  "user/fetchUser",
  async () => {
    const response = await axios.get("/api/users/me")
    return response.data["data"]["attributes"]
  },
)

const userSlice = createSlice({
  name: "user",
  initialState: {
    user: null,
    fetching: true,
  } as UserState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.user = action.payload
        state.fetching = false
      })
      .addCase(fetchUser.rejected, (state) => {
        state.user = null
        state.fetching = false
      })
      .addCase(fetchUser.pending, (state) => {
        state.fetching = true
      })
  },
})

export const { setUser } = userSlice.actions
export const getUser = (state: RootState) => state.userReducer.user
export const getUserFetching = (state: RootState) => state.userReducer.fetching

export const userReducer = userSlice.reducer

/* ===== ===== SPACES ===== ===== */

const spacesSlice = createSlice({
  name: "spaces",
  initialState: {
    spaces: [],
    spaceName: null,
  } as SpacesState,
  reducers: {
    setSpaces: (state, action) => {
      const oldSpaces = state.spaces
      const newSpaces = action.payload.map((updated: Space) => {
        const old = oldSpaces.find((old) => old.name === updated.name)
        return { ...old, ...updated }
      })

      state.spaces = newSpaces
    },
    setSpaceName: (state, action) => {
      state.spaceName = action.payload
    },
    updateSpace: (state, action) => {
      const { name, changes } = action.payload
      const indexOfSpace = state.spaces.findIndex(
        (space) => space.name === name,
      )
      // Spaces we don't know yet (e.g. one just created) are added, but only
      // from a full payload: partial updates such as the Meet participant
      // count can arrive before the spaces list has loaded.
      if (indexOfSpace === -1) {
        if (changes.users) state.spaces.push(changes)
        return
      }

      const existingSpace = state.spaces[indexOfSpace]
      const updatedSpace = { ...existingSpace, ...changes }

      if (isEqual(existingSpace, updatedSpace)) return

      state.spaces.splice(indexOfSpace, 1, updatedSpace)
    },
  },
})

export const { setSpaces, setSpaceName, updateSpace } = spacesSlice.actions

export const getSpaces = (state: RootState) => state.spacesReducer.spaces
export const getSpaceName = (state: RootState) => state.spacesReducer.spaceName
export const getCurrentSpace = createSelector(
  getSpaces,
  getSpaceName,
  (spaces, spaceName) =>
    spaces ? spaces.find((space) => space.name === spaceName) : null,
)

export const spacesReducer = spacesSlice.reducer

/* ===== ===== NOTIFICATIONS ===== ===== */

export const AlertSlice = createSlice({
  name: "alert",
  initialState: {
    alerts: [],
  } as AlertsState,
  reducers: {
    createAlert: (state, action) => {
      state.alerts.push({
        message: action.payload.message,
        type: action.payload.type,
      })
    },
  },
})

export const { createAlert } = AlertSlice.actions
export const getAlerts = (state: RootState) => state.alertsReducer.alerts
export const alertsReducer = AlertSlice.reducer

/* ===== ===== COUNTDOWN ===== ===== */

// NOT YET IN USE

// const countdownSlice = createSlice({
//   name: "countdown",
//   initialState: {
//     countdownInterval: null,
//   },
//   reducers: {
//     setInterval: (state) => {
//       state.countdownInterval
//     },
//   },
// })

/* ===== ===== STORE ===== ===== */

export const reducer = {
  cycleReducer: cycleReducer,
  userReducer: userReducer,
  spacesReducer: spacesReducer,
  alertsReducer: alertsReducer,
}

export const store = configureStore({
  reducer,
})

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export const useAppDispatch = () => useDispatch<AppDispatch>()
